import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueries, UseQueryResult } from '@tanstack/react-query';
import { ContentQueryMap, ContentTypeRoutes } from 'src/types/content';
import type { APIResponseCollection, ContentType } from 'src/types/content';
import useApi from './useApi';
import { Content } from 'src/api/Content';
import { useCallback } from 'react';

// Approach from https://medium.com/@frederik_62300/a-cleaner-way-to-build-frontends-with-strapi-react-query-92d731901ddd

export function useContentCollection(contentType, query) {
  const $ = _c(10);
  const contentApi = useApi(Content);
  const t0 = query || "list";
  let t1;
  if ($[0] !== contentType || $[1] !== t0) {
    t1 = [contentType, t0];
    $[0] = contentType;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== contentApi || $[4] !== contentType || $[5] !== query) {
    t2 = async () => {
      const queryParam = query || ContentQueryMap[contentType]?.list || "?populate=*";
      const apiRoute = ContentTypeRoutes[contentType];
      const url = `/${apiRoute}${queryParam}`;
      const res = await contentApi.getContentCollection(url);
      if (!res.ok) {
        throw new Error("Failed to fetch");
      }
      return res.data;
    };
    $[3] = contentApi;
    $[4] = contentType;
    $[5] = query;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1 || $[8] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useQuery(t3);
}
export function useMultipleContentCollections(queryRecords) {
  const $ = _c(6);
  const contentApi = useApi(Content);
  const combineData = _temp5;
  let t0;
  if ($[0] !== queryRecords) {
    t0 = Object.keys(queryRecords);
    $[0] = queryRecords;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = t0 as K[];
  let t2;
  if ($[2] !== contentApi || $[3] !== queryRecords || $[4] !== t1) {
    t2 = {
      queries: t1.map(contentType => {
        const queryParam = queryRecords[contentType] || ContentQueryMap[contentType]?.list || "?populate=*";
        const apiRoute = ContentTypeRoutes[contentType];
        const url = `/${apiRoute}${queryParam}`;
        return {
          queryKey: [contentType, queryParam],
          queryFn: async () => {
            const res = await contentApi.getContentCollection(url);
            if (!res.ok) {
              throw new Error("Failed to fetch");
            }
            return res.data;
          }
        };
      }),
      combine: combineData
    };
    $[2] = contentApi;
    $[3] = queryRecords;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return useQueries(t2);
}
function _temp5(results) {
  return {
    data: results.map(_temp).filter(_temp2),
    isLoading: results.some(_temp3),
    isError: results.some(_temp4)
  };
}
function _temp4(result_1) {
  return result_1.isError;
}
function _temp3(result_0) {
  return result_0.isLoading;
}
function _temp2(data) {
  return !!data;
}
function _temp(result) {
  return result.data;
}
export function useContentSingle(contentType, documentId, t0) {
  const $ = _c(11);
  const enabled = t0 === undefined ? true : t0;
  const contentApi = useApi(Content);
  let t1;
  if ($[0] !== contentType || $[1] !== documentId) {
    t1 = [contentType, documentId];
    $[0] = contentType;
    $[1] = documentId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== contentApi || $[4] !== contentType || $[5] !== documentId) {
    t2 = async () => {
      const query = ContentQueryMap[contentType]?.single || "?populate=*";
      const apiRoute = ContentTypeRoutes[contentType];
      const url = `/${apiRoute}/${documentId}${query}`;
      const res = await contentApi.getContentSingle(url);
      if (!res.ok) {
        throw new Error("Failed to fetch");
      }
      return res.data;
    };
    $[3] = contentApi;
    $[4] = contentType;
    $[5] = documentId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== enabled || $[8] !== t1 || $[9] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled
    };
    $[7] = enabled;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return useQuery(t3);
}
export function useContentSingleEntry(contentType, t0, t1) {
  const $ = _c(10);
  const locale = t0 === undefined ? "en" : t0;
  const enabled = t1 === undefined ? true : t1;
  const contentApi = useApi(Content);
  let t2;
  if ($[0] !== contentType) {
    t2 = [contentType];
    $[0] = contentType;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== contentApi || $[3] !== contentType || $[4] !== locale) {
    t3 = async () => {
      const query = ContentQueryMap[contentType]?.single || "?populate=*";
      const localeParam = `&locale=${locale}`;
      const apiRoute = ContentTypeRoutes[contentType];
      const url = `/${apiRoute}${query}${localeParam}`;
      const res = await contentApi.getContentSingle(url);
      if (!res.ok) {
        throw new Error("Failed to fetch");
      }
      return res.data;
    };
    $[2] = contentApi;
    $[3] = contentType;
    $[4] = locale;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== enabled || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      enabled
    };
    $[6] = enabled;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
}